import {
  qPatientFamilyHistory,
  qPatientFamilyMembers,
  qPatientFamilyMemberAllergies,
  qPatientFamilyMemberMedications,
  groupFamilyHistory,
  summarizeFamilyHistory,
  type FamilyHistoryVM,
  type FamilyMemberVM,
  type PatientFamilyHistoryRow,
  type PatientFamilyMemberRow,
  type PatientFamilyMemberAllergyRow,
  type PatientFamilyMemberMedicationRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';
import { NavIcon } from './shared';

/**
 * PRD.267 — read-only Family Allergies & Medications display, grouped by family
 * member. Rendered in the patient workspace/sidebar, visually separated from
 * the patient's OWN allergies and medications. Family records here never appear
 * in the patient's own allergy alert chips or medication list.
 */

function titleCase(v: string): string {
  return v
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function memberTitle(m: FamilyMemberVM): string {
  const rel =
    m.relationshipType === 'OTHER' && m.relationshipLabel
      ? m.relationshipLabel
      : titleCase(m.relationshipType || 'Family member');
  return m.name ? `${rel} — ${m.name}` : rel;
}

function FamilyHistoryHeader({ onEdit }: { onEdit?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
        Family History
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: '1px var(--sp-2)',
        }}
      >
        Not the patient’s own allergies/medications
      </span>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          title="Edit"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 'var(--radius-full)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            padding: 0,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bg-surface-2)';
            e.currentTarget.style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
        >
          <NavIcon name="edit" />
        </button>
      )}
    </div>
  );
}

function FamilyHistoryBody({ history }: { history: FamilyHistoryVM }) {
  const summary = summarizeFamilyHistory(history);

  if (summary.kind === 'empty') {
    return (
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {summary.text}
      </span>
    );
  }

  return (
    <>
      {summary.members.map((m) => (
        <div
          key={m.id}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-1)',
          }}
        >
          <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
            {memberTitle(m)}
          </span>
          {m.allergies.map((a) => (
            <span
              key={a.id}
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}
            >
              Allergy:{' '}
              {[a.substance, a.reaction, a.severity && titleCase(a.severity)]
                .filter(Boolean)
                .join(' — ')}
            </span>
          ))}
          {m.medications.map((med) => (
            <span
              key={med.id}
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}
            >
              Medication:{' '}
              {[med.name, med.dose, med.status && titleCase(med.status)]
                .filter(Boolean)
                .join(' — ')}
            </span>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Read-only family history display.
 *
 * - `variant="section"` (default): appended below the patient's own allergies
 *   in the Allergies context tab, separated by a top border.
 * - `variant="card"`: a standalone, compact sidebar card for the patient
 *   workspace/Overview summary.
 */
export function FamilyHistoryView({
  history,
  variant = 'section',
  onEdit,
}: {
  history: FamilyHistoryVM;
  variant?: 'section' | 'card';
  onEdit?: () => void;
}) {
  if (variant === 'card') {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          padding: 'var(--sp-4)',
          background: 'var(--bg-surface)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <FamilyHistoryHeader onEdit={onEdit} />
        <FamilyHistoryBody history={history} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
        paddingTop: 'var(--sp-4)',
        marginTop: 'var(--sp-4)',
      }}
    >
      <FamilyHistoryHeader onEdit={onEdit} />
      <FamilyHistoryBody history={history} />
    </div>
  );
}

/** Data-loading wrapper: reads the four family tables and renders the view. */
export function FamilyHistorySection({
  patientId,
  variant = 'section',
  onEdit,
}: {
  patientId: string;
  variant?: 'section' | 'card';
  onEdit?: () => void;
}) {
  const { data: historyRows = [] } = usePowerSyncQuery<PatientFamilyHistoryRow>(
    qPatientFamilyHistory(patientId),
  );
  const { data: memberRows = [] } = usePowerSyncQuery<PatientFamilyMemberRow>(
    qPatientFamilyMembers(patientId),
  );
  const { data: allergyRows = [] } = usePowerSyncQuery<PatientFamilyMemberAllergyRow>(
    qPatientFamilyMemberAllergies(patientId),
  );
  const { data: medicationRows = [] } = usePowerSyncQuery<PatientFamilyMemberMedicationRow>(
    qPatientFamilyMemberMedications(patientId),
  );

  const history = groupFamilyHistory(historyRows[0], memberRows, allergyRows, medicationRows);
  return <FamilyHistoryView history={history} variant={variant} onEdit={onEdit} />;
}
